import React, { useContext, useEffect, useMemo, useState } from "react";
import CustomCalendar from "../Components/Calender";
import { useNavigate } from "react-router-dom";
import {
  ChevronsRight,
  Heart,
  HeartPulse,
  Smile,
  UserRound,
  Clock,
} from "lucide-react";
import { GlobalContext } from "../Context/GlobalContext";
import { images } from "../Assets/images";
import storage from "../Assets/js/Global";
import Chart from "react-apexcharts";
import { getWellnessLabel } from "../utils";
const History = () => {
  const {
    getScanHistory,
    scanHistory,
    translatedLabels,
    getHistoryDates,
    historyDates,
  } = useContext(GlobalContext);
  const screenLabels = translatedLabels?.common;
  const storedDate = storage.get("lastSelectedDate");
  const initialDate = storedDate ? new Date(parseInt(storedDate)) : new Date();
  const [selectedDate, setSelectedDate] = useState(initialDate);

  const formatDateTranslated = (date, screenLabels) => {
    const weekdays = [
      screenLabels?.sunday?.label_value || "Sunday",
      screenLabels?.monday?.label_value || "Monday",
      screenLabels?.tuesday?.label_value || "Tuesday",
      screenLabels?.wednesday?.label_value || "Wednesday",
      screenLabels?.thursday?.label_value || "Thursday",
      screenLabels?.friday?.label_value || "Friday",
      screenLabels?.saturday?.label_value || "Saturday",
    ];

    const months = [
      screenLabels?.jan?.label_value || "January",
      screenLabels?.feb?.label_value || "February",
      screenLabels?.march?.label_value || "March",
      screenLabels?.april?.label_value || "April",
      screenLabels?.may?.label_value || "May",
      screenLabels?.june?.label_value || "June",
      screenLabels?.july?.label_value || "July",
      screenLabels?.aug?.label_value || "August",
      screenLabels?.sep?.label_value || "September",
      screenLabels?.oct?.label_value || "October",
      screenLabels?.nov?.label_value || "November",
      screenLabels?.dec?.label_value || "December",
    ];

    const weekday = weekdays[date.getDay()];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    return `${weekday}, ${month} ${day}, ${year}`;
  };
  const formatDateApi = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  useEffect(() => {
    getHistoryDates();
  }, [getHistoryDates]);

  const formattedDates = historyDates
    .map((item) => {
      if (!item.converted_time) return null;
      const date = new Date(item.converted_time.replace(" ", "T"));
      // const date = new Date(item.created_ts * 1000);
      return date.toDateString();
    })
    .filter(Boolean);

  const uniqueDates = [...new Set(formattedDates)];

  useEffect(() => {
    storage.set("lastSelectedDate", selectedDate.getTime());
    getScanHistory(formatDateApi(selectedDate));
  }, [selectedDate, getScanHistory]);

  function getIconForHeartMetric(metric) {
    switch (metric) {
      case "heart_rate":
        return <Heart size={18} />;
      case "breathing_rate":
        return (
          <img src={images.breathIcon} height={20} width={20} alt="breath" />
        );
      case "prq":
        return <img src={images.prqIcon} height={20} width={20} alt="PRQ" />;
      case "sdnn":
      case "hrv_sdnn":
        return <HeartPulse size={20} />;
      default:
        return <Smile size={20} />;
    }
  }
  let navigate = useNavigate();

  function getLabelForMetric(key) {
    return (
      screenLabels?.[key]?.label_value ||
      key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    );
  }

  const handleClick = (scan) => {
    storage.set("scanId", scan?.unique_key);
    storage.set("scanDate", scan.created_ts);
    storage.set("scanGuest", true);
    storage.set("guestEmail", scan?.email);
    navigate("/user-scan-details", {
      state: {
        selectedDate: scan?.created_ts,
        uniqueKey: scan?.unique_key,
      },
    });
  };
  const wellnessValue = 9;

  const options = {
    chart: { type: "radialBar", offsetY: 0, sparkline: { enabled: true } },
    plotOptions: {
      radialBar: {
        strokeWidth: "100",
        startAngle: -150,
        endAngle: 150,
        hollow: { size: "85%", background: "transparent" },
        track: { background: "#f0f0f0", strokeWidth: "100%", margin: 0 },
        dataLabels: {
          name: { offsetY: 50, show: false, color: "#574f8a" },
          value: {
            offsetY: 5,
            color: "#574f8a",
            fontWeight: 600,
            show: true,
            formatter: () => `${wellnessValue}/10`,
          },
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "horizontal",
        shadeIntensity: 0.4,
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 25, 50, 75, 100],
        colorStops: [
          { offset: 0, color: "#00d4ff", opacity: 1 },
          { offset: 25, color: "#7c3aed", opacity: 1 },
          { offset: 50, color: "#ec4899", opacity: 1 },
          { offset: 75, color: "#f97316", opacity: 1 },
          { offset: 100, color: "#ef4444", opacity: 1 },
        ],
      },
    },
    stroke: { lineCap: "round" },
    labels: [getWellnessLabel(wellnessValue, screenLabels)],
  };
  let chartVal = 9;
  const series = useMemo(() => {
    const val = Number(9) || 0;
    return [Math.max(0, Math.min(10, val)) * 10];
  }, []);
  return (
    <div className="content-part">
      <CustomCalendar
        onDateChange={setSelectedDate}
        highlightedDates={uniqueDates}
      />

      <div className="d-flex justify-content-between px-2">
        <div className="d-flex gap-2 align-items-center">
          <div className="div-box bg-lightgray"></div>
          {screenLabels?.history_date_ind?.label_value || "Dates with history"}
        </div>
        <div className="d-flex gap-2 align-items-center">
          <div className="div-box bg-primary"></div>
          {screenLabels?.selected_day?.label_value || "Selected day"}
        </div>
      </div>
      <div className="history-date-part mt-3 mb-3">
        <div className="text-center fw-semibold">
          {/* {formatDate(selectedDate)} */}
          {formatDateTranslated(selectedDate, screenLabels)}
        </div>
      </div>

      {scanHistory?.list && scanHistory.list.length > 0 ? (
        [...scanHistory.list]
          .sort((a, b) => {
            const dateB = b.converted_time
              ? new Date(b.converted_time.replace(" ", "T"))
              : new Date(b.created_ts * 1000 || 0);
            const dateA = a.converted_time
              ? new Date(a.converted_time.replace(" ", "T"))
              : new Date(a.created_ts * 1000 || 0);
            return dateB - dateA;
          })
          .map((scan, index) => {
            const convertedDate = scan?.converted_time
              ? new Date(scan.converted_time.replace(" ", "T"))
              : null;
            const createScanDate = convertedDate
              ? convertedDate.toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })
              : "";

            const scanData = scan?.scan_data || {};

            const wellnessData = {
              value: scanData?.body?.wellness_score || 0,
              status: scanData?.basic_vital_signs?.wellness_level || "N/A",
            };

            const heartData = {
              fatigue_index: {
                value: scanData?.body?.fatigue_index,
                unit: "",
              },
              body_shape_index: {
                value: scanData?.body?.body_shape_index,
                unit: "",
              },
              heart_rate: {
                value: scanData?.heart_health?.heart_rate,
                unit: "bpm",
              },
              hrv_sdnn: {
                value: scanData?.heart_health?.hrv_sdnn,
                unit: "ms",
              },
              rmssd: {
                value: scanData?.stress?.rmssd,
                unit: "ms",
              }, // Temp hide
            };
            const heartMetrics = Object.entries(heartData).map(([key, obj]) => {
              return {
                label: getLabelForMetric(key),
                value: obj?.value ? `${obj.value} ${obj.unit || ""}` : "N/A",
                icon: getIconForHeartMetric(key),
              };
            });

            return (
              <div key={index} className="wellness-card p-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="fw-semibold fs-14px text-primary">
                    <span>🟡</span>{" "}
                    {screenLabels?.wellness_score?.label_value ||
                      "Wellness Score"}
                    :{" "}
                    <span className="text-dark">{wellnessData?.value}/10</span>
                  </div>
                  <div className="text-muted small">{createScanDate}</div>
                </div>
                <hr className="my-1" />

                <div className="metrics">
                  {heartMetrics.map((m, i) => (
                    <div
                      key={i}
                      className="d-flex justify-content-between border-bottom py-1"
                      style={{ fontSize: "14px" }}
                    >
                      <div>
                        <span className="me-2">{m.icon}</span>
                        {m.label}
                      </div>
                      <div className="fw-semibold">{m.value}</div>
                    </div>
                  ))}
                </div>

                <div className="text-center mt-3">
                  {/* <Link
                    to="/user-scan-details"
                    className="fw-semibold text-decoration-none linkColor"
                    state={{
                      selectedDate: scan?.created_ts,
                      uniqueKey: scan?.unique_key,
                    }}
                  >
                    {screenLabels?.view_more?.label_value || "View More"}
                  </Link> */}

                  <button
                    className="fw-semibold text-decoration-none linkColor btn btn-link"
                    onClick={() => handleClick(scan)}
                  >
                    {screenLabels?.view_more?.label_value || "View More"}
                  </button>
                </div>
              </div>
            );
          })
      ) : (
        // <p className="text-center text-muted mt-5">
        //   {screenLabels?.no_history?.label_value ||
        //     "No history records found for the selected date."}
        // </p>

        <div
          className="mt-3 guest-history-card p-3"
          onClick={() => navigate("/guest-scan")}
        >
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <div className="guest-avatar-circle">
                <UserRound size={22} className="text-primary" />
              </div>
              <div>
                <h6 className="fw-bold m-0 text-dark">Nirav Thanki</h6>
                <span className="text-muted small">nirv@yopmail.com</span>
              </div>
            </div>

            <div className="guest-chart-wrapper">
              <Chart
                key={chartVal}
                options={options}
                series={series}
                type="radialBar"
                height={70}
                width={70}
              />
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="badge bg-light text-muted fw-normal d-flex align-items-center gap-1">
              <Clock size={12} /> 7:20 PM
            </span>
            <ChevronsRight size={18} className="text-muted" />
          </div>
        </div>
      )}
    </div>
  );
};

export default History;
